import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Code2, Filter, ScrollText, Search, X } from 'lucide-react'
import { Fragment, useState, type KeyboardEvent } from 'react'

import { getAudit, type AuditEntry } from '../api/admin'
import { PageHeader } from '../components/layout'
import {
  Avatar,
  Badge,
  Button,
  Chip,
  EmptyState,
  IconButton,
  IdChip,
  Input,
  KeyValue,
  Pager,
  Panel,
  Select,
  Table,
  TableSkeleton,
  cx,
  describeError,
  statusLabel,
  tableClass,
  type Tone,
} from '../components/ui/Ui'
import styles from './Audit.module.css'

const ENTITY_TYPES = ['', 'withdrawal', 'master', 'user', 'setting', 'announcement', 'network', 'network_asset', 'market', 'kyc_application', 'signal', 'invite_code'] as const

/** What an entity type is called in the panel. */
const ENTITY_RU: Record<string, string> = {
  withdrawal: 'Вывод',
  master: 'Мастер',
  user: 'Пользователь',
  setting: 'Настройка',
  announcement: 'Объявление',
  network: 'Сеть',
  network_asset: 'Актив в сети',
  market: 'Рынок',
  kyc_application: 'Заявка KYC',
  signal: 'Сигнал',
  invite_code: 'Код приглашения',
  deposit: 'Депозит',
  session: 'Сессия',
  position: 'Позиция',
  referral_qualification: 'Квалификация партнёра',
  referral_settings: 'Партнёрская программа',
  referral_reward: 'Партнёрское вознаграждение',
}

/** The actions in words; an unknown action shows as its code. */
const ACTION_RU: Record<string, string> = {
  'auth.login': 'Вход',
  'auth.logout': 'Выход',
  'auth.login_failed': 'Неудачный вход',
  'auth.refresh_reuse_detected': 'Повтор токена обновления',
  'auth.register': 'Регистрация',
  'auth.password_reset_requested': 'Запрошен сброс пароля',
  'auth.password_reset': 'Пароль сброшен',
  'auth.email_verified': 'Почта подтверждена',
  'security.totp_enabled': '2FA включена',
  'security.totp_disabled': '2FA выключена',
  'security.session_revoked': 'Сессия завершена',
  'security.other_sessions_revoked': 'Другие сессии завершены',
  'security.recovery_codes_regenerated': 'Новые коды восстановления',
  'settings.put': 'Настройка изменена',
  'registry.network': 'Сеть изменена',
  'registry.network_asset': 'Актив в сети изменён',
  'registry.market': 'Рынок изменён',
  'registry.spot_fees': 'Комиссии спота',
  'withdrawal.first_approve': 'Первая подпись вывода',
  'withdrawal.approve': 'Вывод одобрен',
  'withdrawal.auto_approve': 'Автовывод одобрил',
  'withdrawal.auto_skip': 'Автовывод пропустил',
  'withdrawal.reject': 'Вывод отклонён',
  'withdrawal.send': 'Вывод отправлен вручную',
  'withdrawal.send_busy': 'Отправка занята',
  'withdrawal.sent': 'Вывод отправлен',
  'withdrawal.fail': 'Вывод не удался',
  'withdrawal.broadcast_signed': 'Транзакция подписана',
  'withdrawal.broadcast_failed': 'Трансляция не удалась',
  'withdrawal.broadcast_late': 'Трансляция с опозданием',
  'withdrawal.broadcast_unknown': 'Итог трансляции неизвестен',
  'announcement.create': 'Объявление создано',
  'announcement.update': 'Объявление изменено',
  'announcement.delete': 'Объявление удалено',
  'invite_code.create': 'Код приглашения создан',
  'invite_code.revoke': 'Код приглашения отозван',
  'signal.create': 'Сигнал создан',
  'signal.update': 'Сигнал изменён',
  'signal.publish': 'Сигнал опубликован',
  'signal.cancel': 'Сигнал отменён',
  'signal.delete': 'Сигнал удалён',
  'signal.channel_post': 'Сигнал отправлен в канал',
  'user.patch': 'Пользователь изменён',
  'kyc.application.submitted': 'KYC подана',
  'kyc.application.status_changed': 'Статус KYC изменён',
  'kyc.documents.uploaded': 'Документы KYC загружены',
  'ledger.adjustment': 'Корректировка баланса',
  'deposit.reversal_applied': 'Сторнирование применено',
  'deposit.reversal_dismissed': 'Сторнирование отклонено',
  'futures.liquidate': 'Ликвидация позиции',
  'referral.qualification.approve': 'Квалификация одобрена',
  'referral.qualification.reject': 'Квалификация отклонена',
  'referrals.settings.patch': 'Условия партнёрки изменены',
  'referrals.deposit_tiers.replace': 'Таблица процентов изменена',
  'telegram.link': 'Telegram привязан',
  'telegram.unlink': 'Telegram отвязан',
  'master.create': 'Мастер создан',
  'master.profile': 'Профиль мастера',
  'master.strategy': 'Стратегия мастера',
  'master.strategy_status': 'Статус стратегии',
  'master.display_stats': 'Цифры карточки',
  'master.display_stats_clear': 'Цифры карточки сброшены',
  'master.equity_recompute': 'Кривая эквити пересчитана',
}

/** Quick filters: an action prefix, the same as typing it. */
const PRESETS: ReadonlyArray<{ label: string; action: string }> = [
  { label: 'Входы', action: 'auth.' },
  { label: 'Безопасность', action: 'security.' },
  { label: 'Выводы', action: 'withdrawal.' },
  { label: 'Настройки', action: 'settings.' },
  { label: 'Реестр', action: 'registry.' },
  { label: 'Сигналы', action: 'signal.' },
  { label: 'Объявления', action: 'announcement.' },
]

function actionTone(action: string): Tone {
  if (/(fail|failed|reject|revoke|revoked|reuse|delete|cancel|liquidate|disabled|dismissed|unknown|late)/.test(action)) return 'down'
  if (/(approve|create|enabled|publish|sent|applied|verified|register|link$)/.test(action)) return 'accent'
  if (/^auth\.(login|logout)$/.test(action)) return 'neutral'
  return 'info'
}

function actionLabel(action: string): string {
  return ACTION_RU[action] ?? action
}

export default function Audit(): JSX.Element {
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')
  const [applied, setApplied] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const list = useQuery({
    queryKey: ['audit', entityType, applied, page],
    queryFn: () => getAudit({ page, entityType: entityType || undefined, action: applied || undefined }),
  })
  const rows = list.data?.items ?? []
  const filtered = entityType !== '' || applied !== ''

  const apply = (value: string): void => {
    setAction(value)
    setApplied(value.trim())
    setPage(1)
  }
  const toggle = (id: string): void =>
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, id: string): void => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggle(id)
    }
  }

  // Newest first, grouped under a line per day.
  let lastDay = ''

  return (
    <>
      <PageHeader
        title="Аудит"
        description="Кто, что и когда изменил. Нажмите на запись, чтобы увидеть, что было и что стало."
        meta={
          list.data ? (
            <Badge tone="neutral" dot={false}>
              <span className="num">{list.data.total.toLocaleString('ru-RU')}</span>&nbsp;{filtered ? 'по фильтру' : 'записей'}
            </Badge>
          ) : null
        }
      />

      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <Select
            value={entityType}
            onChange={(event) => {
              setEntityType(event.target.value)
              setPage(1)
            }}
            style={{ width: 220 }}
            aria-label="Сущность"
          >
            {ENTITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {type === '' ? 'Любая сущность' : `${ENTITY_RU[type] ?? type} · ${type}`}
              </option>
            ))}
          </Select>
          <Input
            mono
            leading={<Search />}
            placeholder="Действие, напр. withdrawal."
            value={action}
            onChange={(event) => setAction(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') apply(action)
            }}
            aria-label="Действие или его начало"
            style={{ width: 280 }}
            trailing={action !== '' ? <IconButton icon={<X />} label="Очистить действие" size="sm" tooltip={false} onClick={() => apply('')} className={styles.clear} /> : undefined}
          />
          <Button icon={<Filter />} onClick={() => apply(action)}>
            Фильтр
          </Button>
          {filtered ? (
            <Button
              variant="ghost"
              onClick={() => {
                setEntityType('')
                apply('')
              }}
            >
              Сбросить
            </Button>
          ) : null}
        </div>
        <div className={styles.presets} role="group" aria-label="Быстрые фильтры по действию">
          {PRESETS.map((preset) => (
            <Chip key={preset.action} pressed={applied === preset.action} onClick={() => apply(applied === preset.action ? '' : preset.action)}>
              {preset.label}
            </Chip>
          ))}
        </div>
      </div>

      <Panel>
        {list.isError ? (
          <EmptyState tone="error" icon={<ScrollText />} title="Журнал не загрузился" description={describeError(list.error)} action={<Button onClick={() => void list.refetch()}>Повторить</Button>} />
        ) : list.isPending ? (
          <TableSkeleton rows={10} headers={['Когда', 'Кто', 'Действие', 'Объект', 'Изменение', 'IP', '']} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ScrollText />}
            title="Записей нет"
            description={filtered ? 'Под этот фильтр ничего не подходит.' : 'Журнал пока пуст.'}
            action={
              filtered ? (
                <Button
                  onClick={() => {
                    setEntityType('')
                    apply('')
                  }}
                >
                  Сбросить фильтр
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table ariaLabel="Журнал аудита" busy={list.isFetching}>
            <thead>
              <tr>
                <th className={styles.timeCol}>Когда</th>
                <th>Кто</th>
                <th>Действие</th>
                <th>Объект</th>
                <th>Изменение</th>
                <th className={tableClass.right}>IP</th>
                <th className={styles.chevCol} aria-label="Подробности" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const day = dayKey(row.createdAt)
                const header = day !== lastDay ? day : null
                lastDay = day
                const expanded = open.has(row.id)
                return (
                  <Fragment key={row.id}>
                    {header ? (
                      <tr className={styles.dayRow}>
                        <td colSpan={7}>{dayLabel(row.createdAt)}</td>
                      </tr>
                    ) : null}
                    <tr
                      className={cx(tableClass.clickable, expanded ? tableClass.selected : '', styles.entryRow)}
                      onClick={() => toggle(row.id)}
                      onKeyDown={(event) => onRowKey(event, row.id)}
                      tabIndex={0}
                      aria-expanded={expanded}
                    >
                      <td className={styles.timeCol}>
                        <span className={cx('num', styles.time)}>{timeOf(row.createdAt)}</span>
                      </td>
                      <td>
                        <div className={styles.actor}>
                          <Avatar name={row.actorEmail ?? (row.actorId ? 'user' : 'система')} size={26} tone={row.actorEmail ? undefined : 'neutral'} />
                          <div className={styles.actorText}>
                            <span className={styles.actorName}>{row.actorEmail ?? (row.actorId ? short(row.actorId) : 'система')}</span>
                            {row.actorRole ? <span className={tableClass.sub}>{statusLabel(row.actorRole)}</span> : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.action}>
                          <Badge size="sm" tone={actionTone(row.action)}>
                            {actionLabel(row.action)}
                          </Badge>
                          <code className={styles.actionCode}>{row.action}</code>
                        </div>
                      </td>
                      <td>
                        {row.entityType ? (
                          <div className={styles.entity}>
                            <span className={styles.entityType}>{ENTITY_RU[row.entityType] ?? row.entityType}</span>
                            {row.entityId ? <IdChip value={row.entityId} full={row.entityId.length <= 24} head={8} tail={4} label="id" /> : null}
                          </div>
                        ) : (
                          <span className={tableClass.muted}>—</span>
                        )}
                      </td>
                      <td className={styles.summaryCell}>
                        <span className={styles.summary}>{changeSummary(row.before, row.after)}</span>
                      </td>
                      <td className={cx(tableClass.num, tableClass.muted)}>{row.ip ? row.ip.replace(/\/32$/, '') : '—'}</td>
                      <td className={styles.chevCol}>
                        <ChevronRight className={cx(styles.chev, expanded ? styles.chevOpen : '')} aria-hidden="true" />
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className={styles.detailRow}>
                        <td colSpan={7}>
                          <AuditDetail entry={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </Table>
        )}
        {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
      </Panel>
    </>
  )
}

/* ------------------------------------------------------------------------ */
/* detail                                                                   */
/* ------------------------------------------------------------------------ */

type Change = { key: string; before: unknown; after: unknown; kind: 'changed' | 'added' | 'removed' | 'same' }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Field by field: what was, what became. Non-objects compare as one value. */
function diff(before: unknown, after: unknown): Change[] {
  if (isRecord(before) || isRecord(after)) {
    const a = isRecord(before) ? before : {}
    const b = isRecord(after) ? after : {}
    const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]))
    return keys.map((key) => {
      const inA = key in a
      const inB = key in b
      const kind: Change['kind'] = !inA ? 'added' : !inB ? 'removed' : JSON.stringify(a[key]) === JSON.stringify(b[key]) ? 'same' : 'changed'
      return { key, before: a[key], after: b[key], kind }
    })
  }
  if (before === null && after === null) return []
  return [{ key: 'значение', before, after, kind: JSON.stringify(before) === JSON.stringify(after) ? 'same' : 'changed' }]
}

function show(value: unknown): string {
  if (value === undefined) return '—'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

function AuditDetail({ entry }: { entry: AuditEntry }): JSX.Element {
  const [raw, setRaw] = useState(false)
  const changes = diff(entry.before, entry.after)
  const created = entry.before === null && entry.after !== null
  return (
    <div className={styles.detail}>
      <div className={styles.detailMeta}>
        <KeyValue
          columns={2}
          labelWidth={120}
          items={[
            { label: 'Действие', value: <code className={styles.mono}>{entry.action}</code> },
            { label: 'Время', value: new Date(entry.createdAt).toLocaleString('ru-RU'), mono: true },
            { label: 'Кто', value: entry.actorEmail ?? 'система', hint: entry.actorId ? <IdChip value={entry.actorId} bare label="id" /> : undefined },
            { label: 'Роль', value: entry.actorRole ? statusLabel(entry.actorRole) : '—' },
            entry.entityType ? { label: 'Объект', value: ENTITY_RU[entry.entityType] ?? entry.entityType, hint: entry.entityId ? <IdChip value={entry.entityId} full label="id" /> : undefined } : null,
            { label: 'IP', value: entry.ip ?? '—', mono: true },
            entry.userAgent ? { label: 'Браузер', value: <span className={styles.ua}>{entry.userAgent}</span>, wide: true } : null,
          ]}
        />
      </div>

      <div className={styles.diffHead}>
        <span className="eyebrow">{created ? 'Создано' : 'Изменение'}</span>
        <Button size="sm" variant="ghost" icon={<Code2 />} aria-pressed={raw} onClick={() => setRaw((value) => !value)}>
          {raw ? 'Скрыть JSON' : 'JSON до и после'}
        </Button>
      </div>

      {changes.length === 0 ? (
        <p className={styles.noDiff}>Запись без данных «до» и «после».</p>
      ) : (
        <div className={styles.diff} role="table" aria-label="Изменения по полям">
          <div className={styles.diffRowHead} role="row">
            <span role="columnheader">Поле</span>
            <span role="columnheader">Было</span>
            <span role="columnheader">Стало</span>
          </div>
          {changes.map((change) => (
            <div key={change.key} className={cx(styles.diffRow, styles[`diff_${change.kind}`])} role="row">
              <span role="cell" className={styles.diffKey}>
                {change.key}
              </span>
              <span role="cell" className={styles.diffBefore}>
                {change.kind === 'added' ? '—' : show(change.before)}
              </span>
              <span role="cell" className={styles.diffAfter}>
                {change.kind === 'removed' ? '—' : show(change.after)}
              </span>
            </div>
          ))}
        </div>
      )}

      {raw ? (
        <div className={styles.rawGrid}>
          <div>
            <span className="eyebrow">До</span>
            <pre className={styles.json}>{JSON.stringify(entry.before, null, 2) ?? 'null'}</pre>
          </div>
          <div>
            <span className="eyebrow">После</span>
            <pre className={styles.json}>{JSON.stringify(entry.after, null, 2) ?? 'null'}</pre>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* helpers                                                                  */
/* ------------------------------------------------------------------------ */

function summary(value: unknown): string {
  if (value === null || typeof value !== 'object') return value === null ? '—' : String(value)
  return Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${String(v).length > 28 ? `${String(v).slice(0, 26)}…` : String(v)}`)
    .join(' · ')
}

/** «value: false → true» for the fields that changed; the old one-line summary for a creation. */
function changeSummary(before: unknown, after: unknown): string {
  if (isRecord(before) && isRecord(after)) {
    const changed = diff(before, after).filter((change) => change.kind !== 'same')
    if (changed.length === 0) return summary(after) || '—'
    const cut = (value: unknown): string => {
      const text = show(value)
      return text.length > 22 ? `${text.slice(0, 20)}…` : text
    }
    return changed
      .slice(0, 3)
      .map((change) => `${change.key}: ${cut(change.before)} → ${cut(change.after)}`)
      .join(' · ')
  }
  return summary(after ?? before) || '—'
}

function short(value: string): string {
  return value.length > 14 ? `${value.slice(0, 8)}…` : value
}

function dayKey(iso: string): string {
  const date = new Date(iso)
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function dayLabel(iso: string): string {
  const date = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  const long = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' })
  if (dayKey(iso) === dayKey(today.toISOString())) return `Сегодня · ${long}`
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return `Вчера · ${long}`
  return `${date.toLocaleDateString('ru-RU', { weekday: 'long' })} · ${long}`
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
